import { groupBy, isNil, isNumber, omit } from 'lodash-es';
import {
  BOARDS_ENUMS,
  CONTRACT_TYPES,
  FILTER_FIELDS,
  FILTER_OPERATOR_ENUMS,
  FILTER_RULE_ENUMS,
  TENANT_NAMES,
} from '@constants';
import {
  fixedNumber,
  formatAmount,
  formatDate,
  timeFromNow,
  formatCurrencyValue,
  extractVat,
  appendVat,
} from '@utils';
import {
  BOOKINGS_STAGES,
  BOOKINGS_STAGES_ORDER,
  BOOKING_LABELS_STYLED,
  contractPeriodOptions,
  contractTypeOptions,
  originOptions,
  stages,
} from 'components/bookings/constants';
import theme from 'config/theme';
import { URLS } from 'layouts/DrawerLayout/constants';
import { AVAILABLE_HISTORY_ITEMS } from 'components/common/InstaTaskHistory/constants';
import { BOARD_META_BY_ID } from '@helpers';
import { formatISO } from 'date-fns';

const getBookingLabels = (bookingLabels = []) => {
  const DEFAULT_LABEL_STYLES = {
    backgroundColor: theme.palette.grays.gray300,
    color: theme.palette.grays.gray900,
    fontSize: '0.75rem',
    height: '15px',
  };

  const matched = bookingLabels.map((labelName) => {
    const predefined = BOOKING_LABELS_STYLED.find(
      ({ label }) => label === labelName
    );

    if (predefined) {
      return { text: predefined.label, styles: predefined.styles };
    }

    return {
      text: labelName,
      styles: DEFAULT_LABEL_STYLES,
    };
  });

  const columns = [];
  for (let i = 0; i < matched.length; i += 2) {
    const col = { 0: matched[i] };

    if (matched[i + 1]) {
      col[1] = matched[i + 1];
    }

    columns.push(col);
  }

  return columns;
};

export const getBookingsCardColumns = (tasks = []) => {
  const order = BOOKINGS_STAGES_ORDER;
  const groupedByStatus = groupBy(tasks, 'stage');

  const columns = order.reduce((acc, cur) => {
    acc.push({
      id: cur,
      title: BOOKINGS_STAGES[cur]?.label,
      cards: groupedByStatus[cur] || [],
      styles: BOOKINGS_STAGES[cur]?.styles,
      value: true,
      label: BOOKINGS_STAGES[cur]?.label,
      key: cur,
    });
    return acc;
  }, []);

  return columns;
};

export const handleBookings = (
  data = [],
  assigneesMap = {},
  enumsData = {}
) => {
  const customerTypesEnums = enumsData?.customerTypes || {};

  return data.map((booking) => {
    const contractType =
      booking?.contractType === CONTRACT_TYPES.open ? 'Open' : 'Fixed';
    const date = booking?.created_at
      ? formatDate(booking?.created_at, 'dd/MM/yyyy')
      : '-';
    const contractPeriod = `${booking.contract_period} Month`;

    let assignee;
    const assigneeEmail = booking.ticketDetails?.assignee;
    if (!assigneeEmail) {
      assignee = {
        value: 'none',
        text: 'None',
        label: 'None',
      };
    } else if (assigneesMap[assigneeEmail]) {
      assignee = assigneesMap[assigneeEmail];
    } else {
      assignee = {
        value: assigneeEmail,
        text: assigneeEmail,
        label: assigneeEmail,
      };
    }

    const displayProducts = (booking?.products || []).filter(
      (product) => product.display === true
    );

    return {
      ...booking,
      id: booking?.id,
      href: `${URLS.bookings}/${booking?.id}`,
      description: booking?.vehicle?.title || '-',
      title: `${booking?.first_name} ${booking?.last_name}`,
      stage: booking.stage,
      date,
      contractType,
      assignee,
      fullName: `${booking?.first_name} ${booking?.last_name}`,
      created_at: date,
      updated_at: booking?.updated_at
        ? formatDate(booking?.updated_at, 'dd/MM/yyyy')
        : '',
      timeAgo: timeFromNow(booking?.created_at),
      email: booking?.email,
      pipeDriveId: booking?.pipedrive?.id,
      pipeDriveUrl: booking?.pipedrive?.url,
      is_active: booking?.is_active || '',
      region: booking?.region || '-',
      contract: booking?.contract || '-',
      contractPeriod,
      phoneNumber: booking?.phone_number || '-',
      vehicle: booking?.vehicle?.title || '-',
      origin: booking?.origin,
      skuId: booking?.vehicle?.id,
      tenant: TENANT_NAMES[booking?.tenant],
      price: booking?.products?.[1]?.finalPrice?.number
        ? fixedNumber(booking?.products?.[1]?.finalPrice?.number)
        : '',
      fee: booking?.products?.[1]?.finalPrice?.number
        ? formatAmount(fixedNumber(booking?.products?.[1]?.finalPrice?.number))
        : '-',
      initialFee: booking?.products?.[1]?.price?.number
        ? formatAmount(fixedNumber(booking?.products?.[1]?.price?.number))
        : '-',
      userInternalId: booking?.userInternalId || '-',
      userId: booking?.user_id,
      labels: [
        {
          text: contractType,
          styles: {
            backgroundColor: theme.palette.yellows.yellow200,
            color: theme.palette.yellows.yellow900,
          },
        },
        {
          text: booking?.origin,
          styles: {
            backgroundColor: theme.palette.blues.primaryBlue200,
            color: theme.palette.blues.primaryBlue800,
          },
        },
        {
          text: contractPeriod,
          styles: {
            backgroundColor: theme.palette.yellows.yellow200,
            color: theme.palette.yellows.yellow900,
          },
        },
      ],
      bookingLabels: getBookingLabels(booking.ticketDetails?.labels || []),
      ticketDetails: booking?.ticketDetails,
      customerTypes:
        booking?.customerTypes?.map((type) => ({
          text: customerTypesEnums[type],
          value: type,
        })) || [],
      contractValue: formatCurrencyValue(booking.contractValue),
      wonTime: booking?.ticketDetails?.wonTime
        ? formatDate(booking?.ticketDetails?.wonTime, 'dd/MM/yyyy HH:mm')
        : '-',
      initialCosts: {
        total: displayProducts.reduce((sum, p) => {
          return sum + appendVat(p?.finalPrice?.number, p?.vat);
        }, 0),
        currency: 'EUR',
        products: displayProducts,
      },
    };
  });
};

export const handleAssigneeData = (data = []) =>
  data
    ?.sort((a, b) => a.firstName.localeCompare(b.firstName))
    ?.map((item) => ({
      isActive: item?.isActive,
      value: item.email,
      text:
        item.firstName && item.lastName
          ? `${item?.firstName} ${item?.lastName}`
          : item.email,
    }));

const getProductFieldRule = ({ value, productSlug, operator }) => {
  return {
    type: FILTER_RULE_ENUMS.MatchesElementRuleType,
    field: 'products',
    operator: null,
    value: [
      {
        field: 'slug',
        operator: FILTER_OPERATOR_ENUMS['='],
        type: FILTER_RULE_ENUMS.ComparableRuleType,
        value: productSlug,
      },
      {
        field: 'final_price',
        operator,
        type: FILTER_RULE_ENUMS.ComparePriceRuleType,
        value,
      },
    ],
  };
};
export const transformFilters = (node) => {
  if (node?.field === 'products.userDeposit') {
    return getProductFieldRule({
      value: node.value,
      operator: node.operator,
      productSlug: 'users-deposit',
    });
  }
  if (node?.field === 'products.netPrice') {
    return getProductFieldRule({
      value: node.value,
      operator: node.operator,
      productSlug: 'monthly-fee',
    });
  }
  if (node?.field === 'products.grossPrice') {
    return getProductFieldRule({
      value: extractVat(node.value, 24).toString(), // assuming VAT is 24%, adjust as necessary
      operator: node.operator,
      productSlug: 'monthly-fee',
    });
  }

  // recursion if there are children/value
  if (Array.isArray(node)) {
    return node.map(transformFilters);
  }

  return node;
};

export const getBookingsPayload = (payload = {}, modalFiltersRules = null) => {
  const fields =
    omit(payload, [
      'page',
      'pageSize',
      'order',
      'orderBy',
      'query',
      'tenant',
      'assignee',
      'ticket_details.assignee',
      'labels',
      'customerTypes',
      'dateFrom',
      'dateTo',
    ]) || {};
  const { tenant } = payload;
  const numberFields = ['stage', 'contract_type'];

  const { dateFrom, dateTo } = payload;

  const value = [
    ...Object.keys(fields).map((field) => ({
      field,
      operator: FILTER_OPERATOR_ENUMS['='],
      type: FILTER_RULE_ENUMS.ComparableRuleType,
      value: numberFields.includes(field)
        ? Number(payload[field])
        : payload[field],
    })),
  ];

  if (payload['ticket_details.assignee']) {
    value.push({
      field: 'ticket_details.assignee',
      operator: FILTER_OPERATOR_ENUMS['='],
      type: FILTER_RULE_ENUMS.ComparableRuleType,
      value:
        payload['ticket_details.assignee'] === 'none'
          ? ''
          : payload['ticket_details.assignee'],
    });
  }

  // bookings without an order id is B2C bookings
  if (!payload['ticket_details.order_id']) {
    value.push({
      field: 'ticket_details.order_id',
      operator: FILTER_OPERATOR_ENUMS['='],
      type: FILTER_RULE_ENUMS.CompareStringRuleType,
      value: '',
    });
  }
  const tenants = Object.keys(TENANT_NAMES);

  // if tenant is instacar or instaride
  if (isNumber(tenant) && tenants.includes(tenant.toString())) {
    value.push({
      field: 'tenant',
      operator: FILTER_OPERATOR_ENUMS['='],
      type: FILTER_RULE_ENUMS.ComparableRuleType,
      value: Number(tenant),
    });
  } else {
    // all tenants
    const allTenantsRule = {
      type: FILTER_RULE_ENUMS.OrRuleType,
      value: tenants.map((field) => ({
        field: 'tenant',
        type: FILTER_RULE_ENUMS.ComparableRuleType,
        operator: FILTER_OPERATOR_ENUMS['='],
        value: Number(field),
      })),
    };
    value.push(allTenantsRule);
  }

  if (payload.labels) {
    value.push({
      field: 'ticket_details.labels',
      operator: FILTER_OPERATOR_ENUMS['='],
      type: FILTER_RULE_ENUMS.IsAnyOfRuleType,
      value: Array.isArray(payload.labels) ? payload.labels : [payload.labels],
    });
  }

  if (payload.customerTypes) {
    value.push({
      field: 'customer_types',
      operator: FILTER_OPERATOR_ENUMS['='],
      type: FILTER_RULE_ENUMS.IsAnyOfRuleType,
      value: Array.isArray(payload.customerTypes)
        ? payload.customerTypes
        : [payload.customerTypes],
    });
  }

  if (modalFiltersRules) {
    value.push(...transformFilters(modalFiltersRules?.value || []));
  }

  if (dateFrom || dateTo) {
    const startDate = dateFrom ? new Date(dateFrom) : new Date(0);
    const endDate = dateTo ? new Date(dateTo) : null;

    const dateRules = [
      {
        type: FILTER_RULE_ENUMS.CompareDateRuleType,
        field: 'ticket_details.wonTime',
        operator: FILTER_OPERATOR_ENUMS['>='],
        value: startDate.getTime(),
      },
    ];

    if (endDate) {
      dateRules.push({
        type: FILTER_RULE_ENUMS.CompareDateRuleType,
        field: 'ticket_details.wonTime',
        operator: FILTER_OPERATOR_ENUMS['<='],
        value: endDate.getTime(),
      });
    }

    value.push({
      type: FILTER_RULE_ENUMS.AndRuleType,
      field: null,
      operator: null,
      value: dateRules,
    });
  }

  return {
    boardType: payload.boardType,
    type: FILTER_RULE_ENUMS.AndRuleType,
    field: null,
    operator: null,
    value,
  };
};

const sortProducts = (products) => {
  const slugArray = [
    'sign-up-fee',
    'monthly-fee',
    'users-deposit',
    'instastart',
    'extra-months',
    'deposit-from-vehicle-sale',
    'price-km',
    'extra-kms',
    'free-fuel',
    'extra-guarantee',
    'deductible-amount',
  ];
  const slugIndexMap = new Map(slugArray.map((str, index) => [str, index]));
  const sortedProductsBySlug = products.sort((a, b) => {
    const indexA = slugIndexMap.has(a.slug)
      ? slugIndexMap.get(a.slug)
      : Infinity;
    const indexB = slugIndexMap.has(b.slug)
      ? slugIndexMap.get(b.slug)
      : Infinity;

    return indexA - indexB; // This sorts based on the index in slugArray
  });
  return sortedProductsBySlug;
};

export const handleProducts = (data) => {
  if (!data?.length) {
    return [];
  }
  const sortedProducts = sortProducts(data);

  const products = sortedProducts?.map((item) => {
    return {
      ...item,
      ...item,
      vat: item.vat ? item.vat * 100 : item.vat,
      customFields: {
        ...item?.customFields,
        initialMonthsLength: Array.isArray(item?.customFields?.months)
          ? item.customFields.months.length
          : 0,
        months: Array.isArray(item?.customFields?.months)
          ? item.customFields.months.map((month) => month)
          : [],
        initialExtraMonthsLength: Array.isArray(item?.customFields?.extraMonths)
          ? item.customFields.extraMonths.length
          : 0,
        extraMonths: Array.isArray(item?.customFields?.extraMonths)
          ? item.customFields.extraMonths.map((month) =>
              typeof month === 'object' && month !== null
                ? month
                : { text: month, value: month }
            )
          : [],
      },
    };
  });

  return products;
};

const handleGuarantors = (guarantors) => {
  if (!guarantors) {
    return [];
  }
  return guarantors;
};

const parseEuroAmount = (amount = '') => {
  return parseFloat(
    amount.replace('€', '').replace(/\./g, '').replace(',', '.')
  );
};
const handleLinkedOffers = (linkedOffers) => {
  if (!linkedOffers) {
    return [];
  }
  const offers = linkedOffers?.map((offer) => {
    return {
      ...offer,
      objectId: offer.id,
    };
  });
  return offers;
};

export const handleBookingData = ({
  booking = {},
  assignees = [],
  bookingLabelsOptions = [],
}) => {
  const assignee = assignees.find(
    (item) => item.email === booking.ticketDetails?.assignee
  );
  const instastartProduct = booking?.products?.find(
    (product) => product.slug === 'instastart'
  );

  const initialPayment = parseEuroAmount(booking.initialPayment);
  const initialPaymentVat = parseEuroAmount(booking.initialPaymentVAT);
  const initialPaymentWithVat = initialPayment + initialPaymentVat;

  return {
    ...booking,
    initialPayment: booking.initialPayment || '',
    initialPaymentVat: initialPaymentWithVat
      ? `${initialPaymentWithVat.toFixed(2)}€`
      : '',
    instastartMonths: instastartProduct?.customFields?.months?.length || '',
    ticketDetails: {
      ...booking.ticketDetails,
      requestDate: booking?.created_at
        ? formatDate(booking?.created_at, 'dd/MM/yyyy')
        : '-',
      assignee: assignee
        ? {
            value: assignee.email,
            text:
              assignee.firstName && assignee.lastName
                ? `${assignee?.firstName} ${assignee?.lastName}`
                : assignee.email,
          }
        : booking.ticketDetails?.assignee,
      labels: (booking.ticketDetails?.labels || []).map((labelValue) => {
        const foundOption = bookingLabelsOptions?.find(
          (option) => option.value === labelValue
        );
        return (
          foundOption || {
            value: labelValue,
            text: labelValue,
            isDisabled: true,
          }
        );
      }),
    },
    promisedDeliveryDate: [
      booking?.promisedDeliveryDate || null,
      booking?.promisedDeliveryDateUntil || null,
    ],
    products: handleProducts(booking?.products),
    linkedOffers: handleLinkedOffers(booking?.linkedOffers),
    guarantorAgreements: handleGuarantors(booking?.guarantorAgreements),
    contractValue: formatCurrencyValue(booking.contractValue),
    initialPaymentDetails: {
      ...booking.initialPaymentDetails,
      paymentAmount: booking.initialPaymentDetails?.paymentAmount?.number ?? '',
    },
    recurringPaymentDetails: {
      ...booking.recurringPaymentDetails,
      paymentAmount:
        booking.recurringPaymentDetails?.paymentAmount?.number ?? '',
    },
  };
};

export const normalizeUserData = (data = {}, booking = {}) => {
  const billingInfo = data?.billingInfo?.[0] || {};
  const lang =
    (data?.lang && (data?.lang === 'el' ? 'Greek' : 'English')) || '-';
  return {
    billingInfo: data?.billingInfo || [],
    id: data.id || '',
    email: data?.email,
    firstName: data?.firstName,
    lastName: data?.lastName,
    fullName: data?.firstName ? `${data?.firstName} ${data?.lastName}` : '-',
    address: billingInfo?.address || '-',
    phoneNumber: data?.phoneNumber || '-',
    tin: billingInfo?.vat || '-',
    taxOffice: billingInfo?.doi || '-',
    city: billingInfo?.city || booking?.region || '-',
    language: lang,
  };
};

export const normalizeVehicleData = (data = {}) => {
  const vehicle = data?.vehicle || {};
  const vehicleDetails = data?.vehicleDetails || {};
  const properties = [];

  if (vehicle.fuel) {
    properties.push(vehicle.fuel);
  }
  if (vehicle.transmission) {
    properties.push(vehicle.transmission);
  }
  if (vehicle.color) {
    properties.push(vehicle.color);
  }
  if (vehicle.year) {
    properties.push(vehicle.year);
  }
  return {
    ...vehicle,
    skuId: vehicle.id,
    plate: vehicleDetails.plate,
    id: vehicleDetails.vehicleInternalId,
    properties,
    internalId: vehicle.vehicle_id,
    vin: vehicleDetails.vin,
    procurement: {
      insuranceProvider: vehicleDetails.insuranceProvider,
      insuranceCompany: vehicleDetails.insuranceCompany,
      vehicleOwnership: vehicleDetails.vehicleOwnership,
      financingProvider: vehicleDetails.financingProvider,
    },
  };
};

export const getUpdateBookingPayload = (values) => {
  let payload = {};

  // if (values.ticketDetails) {
  //   payload = { ...payload, ticketDetails: values.ticketDetails };
  // }
  if (values.initialPaymentDetails) {
    payload = {
      ...payload,
      initialPaymentDetails: {
        ...values.initialPaymentDetails,
        paymentAmount: values.initialPaymentDetails.paymentAmount
          ? {
              number: Number(values.initialPaymentDetails.paymentAmount),
              currency: 'EUR',
            }
          : undefined,
      },
    };
  }
  if (values.recurringPaymentDetails) {
    payload = {
      ...payload,
      recurringPaymentDetails: {
        ...values.recurringPaymentDetails,
        paymentAmount: values.recurringPaymentDetails.paymentAmount
          ? {
              number: Number(values.recurringPaymentDetails.paymentAmount),
              currency: 'EUR',
            }
          : undefined,
      },
    };
  }
  if (values.billingInfo) {
    payload = { ...payload, billingInfo: values.billingInfo };
  }
  if (values.promisedDeliveryDate) {
    payload = {
      ...payload,
      vehicle: {
        promisedDeliveryDate: values.promisedDeliveryDate[0]
          ? formatISO(values.promisedDeliveryDate[0])
          : null,
        promisedDeliveryDateUntil: values.promisedDeliveryDate[1]
          ? formatISO(values.promisedDeliveryDate[1])
          : null,
      },
    };
  }

  if (values.products) {
    payload = {
      ...payload,
      products: values.products.map((item, index) => ({
        ...item,
        order: index,
        discount: item?.discount ? Number(item?.discount) : 0,
        finalPrice: item?.finalPrice?.number
          ? `${item?.finalPrice?.number}`
          : `0`,
        price: item?.price?.number ? `${item?.price?.number}` : null,
        vat: Number(item?.vat) ? Number(item?.vat) / 100 : Number(item?.vat),
        quantity: item?.quantity ? Number(item?.quantity) : 0,
        customFields: {
          ...item?.customFields,
          months: item?.customFields?.months,
          extraMonths: Array.isArray(item?.customFields?.extraMonths)
            ? item.customFields.extraMonths.map((month) =>
                typeof month === 'object' ? month.value : month
              )
            : item?.customFields?.extraMonths,
        },
      })),
    };
  }
  return payload;
};

export const handleOrders = (data = []) =>
  data.map((item) => ({ text: item.orderName, value: item.id }));

export const handleBookingComments = ({
  data,
  loggedInUser,
  getAssigneeName,
}) => {
  if (!data?.length) return [];

  return (
    data?.map((item) => {
      return {
        id: item.id,
        type: 'comment',
        comment: item.comment,
        createdAt: item.createdAt,
        createdBy: item.author,
        userName: getAssigneeName(item.author),
        isLoggedInUser: item.author === loggedInUser?.email,
      };
    }) || []
  );
};

const serializeValue = (value) => {
  if (isNil(value)) {
    return 'empty';
  }

  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return String(value);
  }

  try {
    return JSON.stringify(value);
  } catch {
    return 'empty';
  }
};

export const handleChangeLog = ({ changelog, getAssigneeName }) => {
  return changelog.map((log) => {
    let valueFrom = serializeValue(log.from);
    let valueTo = serializeValue(log.to);

    if (log.field.includes('Assignee')) {
      valueFrom = log.from ? getAssigneeName(log.from) : 'empty';
      valueTo = log.to ? getAssigneeName(log.to) : 'empty';
    }

    return {
      ...log,
      actor: getAssigneeName(log.actor) || log.actor,
      boardType: 'changelog',
      type: 'changelog',
      fieldName: log.field.join('.'),
      valueFrom,
      valueTo,
    };
  });
};

export const handleEmails = ({ emails, getAssigneeName }) => {
  return emails.map((email) => {
    const trimmedBody =
      email?.body?.length > 100
        ? `${email.body.slice(0, 100)}...`
        : email?.body;

    return {
      ...email,
      actor: getAssigneeName(email.actor) || email.actor,
      status: email.status,
      type: 'email',
      subject: email.subject || '(no subject)',
      sender: email?.from?.name || email?.from?.emailAddress,
      snippet: email?.snippet || trimmedBody || '(no body)',
      body: email?.body || '(no body)',
      receivers: email?.to?.map((recipient) => ({
        name: recipient?.name || recipient?.emailAddress,
        email: recipient?.emailAddress,
        id: recipient?.id,
      })),
    };
  });
};

export const handleSMS = ({ sms, getAssigneeName }) => {
  if (!sms?.length) return [];

  return (
    sms?.map((item) => ({
      createdAt: item?.CreatedAt,
      message: item?.Message || '',
      receiver: item?.SentTo || '',
      type: 'sms',
      sender: getAssigneeName(item?.CreatedBy),
    })) || []
  );
};

export const handleInfiniteQueryBookingData = ({
  data,
  loggedInUser,
  handleChangelogItem,
  isSystemActor,
  getAssigneeName,
}) => {
  if (!data || !data?.length) return [];

  return data.map((item) => {
    const type = item?.type;

    if (type === AVAILABLE_HISTORY_ITEMS.changelog) {
      const itemData = item?.data || {};

      const changelogInitialItem = {
        ...itemData,
        fieldName: itemData?.field ?? itemData?.fieldName,
        valueFrom: itemData?.from ?? itemData?.valueFrom,
        valueTo: itemData?.to ?? itemData?.valueTo,
      };

      const changelogItem = handleChangelogItem(changelogInitialItem);

      const changelogActor = changelogItem?.actor || '';

      const actor = isSystemActor(changelogActor)
        ? changelogActor
        : getAssigneeName(changelogActor);

      return {
        type,
        ...itemData,
        ...(changelogItem || {}),
        actor,
      };
    }

    if (type === AVAILABLE_HISTORY_ITEMS.sms) {
      const boardType = item?.data?.boardType;

      const boardMeta = BOARD_META_BY_ID[boardType] || {};

      const resourceId = item?.data?.resourceId || '';

      const ticketUrl =
        boardMeta.url && resourceId ? `${boardMeta.url}/${resourceId}` : null;
      const sender = getAssigneeName(item?.data?.sender);
      return {
        ...(item.data || {}),
        type,
        sender,
        url: ticketUrl,
      };
    }

    if (type === AVAILABLE_HISTORY_ITEMS.comment) {
      const itemData = item?.data || {};

      return {
        type,
        ...itemData,
        isLoggedInUser: itemData?.author === loggedInUser?.email,
        userName: getAssigneeName(itemData.author),
      };
    }

    if (type === AVAILABLE_HISTORY_ITEMS.email) {
      const itemData = item?.data || {};

      const url =
        itemData?.threadId && itemData?.mailboxId
          ? `${URLS.communication}/${itemData?.threadId}?mailboxId=${itemData?.mailboxId}`
          : '';

      const status = itemData?.status || '';

      const sender =
        itemData?.emailSender?.name ||
        `${itemData?.senderFirstName || ''} ${
          itemData?.senderLastName || ''
        }`.trim() ||
        '-';

      const subject = itemData?.subject || '';

      const receivers =
        itemData?.emailReceivers?.length > 0
          ? itemData?.emailReceivers.map((emailReceivers) => {
              return {
                ...emailReceivers,
                id: emailReceivers?.email || '',
                name: emailReceivers?.name || '',
                email: emailReceivers?.email || '',
              };
            })
          : [];
      return {
        ...itemData,
        url,
        type,
        status,
        subject,
        sender,
        receivers,
      };
    }
    return {
      type,
      ...(item?.data || {}),
    };
  });
};

export const handleCustomerTypesEnums = (data) => {
  if (!data?.customerTypes) {
    return [];
  }

  return Object.entries(data?.customerTypes || {}).map(([key, value]) => ({
    text: value,
    value: key,
  }));
};

export const handleLostReasonsEnums = (data) => {
  if (!data?.lostReasons) {
    return [];
  }

  return Object.entries(data?.lostReasons || {}).map(([key, value]) => ({
    id: key,
    text: value,
    label: value,
    value: key,
  }));
};

export const handleBookingLabels = (data = []) => {
  return data.map((option) => ({
    text: option.name,
    value: option.name,
    labelId: option.id,
  }));
};

export const handleLinkedTasks = (data = [], taskStatuses = []) => {
  if (!data?.length) return [];
  const boardValues = Object.values(BOARDS_ENUMS);

  return data.map((item) => {
    const board = boardValues.find((b) => b.board === item?.name);

    const statusName =
      taskStatuses.find((status) => status.id === item.status)?.name ||
      item.status;

    return {
      id: item.id,
      board: board ? board.text : item.boardName,
      ticketId: item.id,
      ticketUrl: BOARD_META_BY_ID[item.name]?.url
        ? `${BOARD_META_BY_ID[item.name].url}/${item.id}`
        : null,
      status: statusName,
      reason: item.reason,
    };
  });
};

export const getBookingFields = ({
  assignees = [],
  bookingLabelsOptions = [],
  products = [],
  customerTypesOptions = [],
  searchPlates,
}) => ({
  created_at: {
    ...FILTER_FIELDS.Date,
    label: 'Created At',
  },
  'ticketDetails.wonTime': {
    ...FILTER_FIELDS.DateTime,
    label: 'Won Time',
  },
  'products.userDeposit': {
    ...FILTER_FIELDS.Price,
    label: 'User Deposit',
  },
  'products.netPrice': {
    ...FILTER_FIELDS.Price,
    label: 'Net Price',
  },
  'products.grossPrice': {
    ...FILTER_FIELDS.Price,
    label: 'Gross Price',
  },
  origin: { ...FILTER_FIELDS.Select, label: 'Origin', values: originOptions },
  contract_type: {
    ...FILTER_FIELDS.Select,
    label: 'Contract Type',
    values: contractTypeOptions,
    valueType: 'number',
  },
  contract_period: {
    ...FILTER_FIELDS.Select,
    label: 'Contract Period',
    values: contractPeriodOptions,
  },
  customer_types: {
    ...FILTER_FIELDS.MultiSelect,
    label: 'Customer Types',
    values: customerTypesOptions,
  },
  stage: { ...FILTER_FIELDS.Select, label: 'Stage', values: stages },
  'ticket_details.assignee': {
    ...FILTER_FIELDS.Autocomplete,
    label: 'Assignee',
    values: assignees,
  },
  'ticket_details.labels': {
    ...FILTER_FIELDS.MultiSelect,
    label: 'Labels',
    values: bookingLabelsOptions,
  },
  email: { ...FILTER_FIELDS.String, label: 'Email (customer)' },
  referral_id: { ...FILTER_FIELDS.String, label: 'Referral ID' },
  'shared_vehicle.plate': {
    ...FILTER_FIELDS.BookingPlate,
    label: 'Plate',
    async: true,
    searchFn: searchPlates,
  },
  'vehicle.vehicle_id': { ...FILTER_FIELDS.String, label: 'Car ID' },
  'vehicle.id': { ...FILTER_FIELDS.String, label: 'SKU' },
  coupon: { ...FILTER_FIELDS.String, label: 'Coupon' },
  phone_number: { ...FILTER_FIELDS.String, label: 'Phone' },
  'billing_info.vat': { ...FILTER_FIELDS.String, label: 'TIN / AFM' },
  id: { ...FILTER_FIELDS.String, label: 'Internal ID' },
  'linked_offers.booking_id': { ...FILTER_FIELDS.String, label: 'Offer ID' },
  'initial_payment_details.iban': { ...FILTER_FIELDS.String, label: 'IBAN' },
  // invoice_type: { ...FILTER_FIELDS.String, label: 'Invoice type' },
  'products.slug': {
    ...FILTER_FIELDS.Select,
    label: 'Product Name',
    values: products,
  },
  created_by: { ...FILTER_FIELDS.String, label: 'Booking Creator' },
});
